import { useCallback, useEffect, useRef, useState } from 'react'
import type { Roll } from '@shared/types'
import { api } from './api'

const POLL_MS = 3000
const MAX_KEPT = 200

/**
 * Keeps a campaign's roll feed current.
 *
 * Polling rather than a socket: a table is five or six people, three seconds is
 * imperceptible at the table, and it needs no Durable Object — which Pages
 * Functions cannot host anyway without a second Worker. Only rolls newer than
 * the last server timestamp are fetched, so each poll is tiny.
 */
export function useRollFeed(campaignId: string, enabled = true) {
  const [rolls, setRolls] = useState<Roll[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const since = useRef(0)

  const merge = useCallback((incoming: Roll[]) => {
    if (incoming.length === 0) return
    setRolls((current) => {
      const seen = new Set(current.map((roll) => roll.id))
      const fresh = incoming.filter((roll) => !seen.has(roll.id))
      if (fresh.length === 0) return current
      return [...fresh, ...current]
        .sort((a, b) => b.createdAt - a.createdAt)
        .slice(0, MAX_KEPT)
    })
  }, [])

  const poll = useCallback(async () => {
    try {
      const res = await api.listRolls(campaignId, since.current)
      since.current = res.serverTime
      merge(res.rolls)
      setError(null)
    } catch {
      // A dropped poll is not worth showing; the next one will catch up.
    } finally {
      setLoading(false)
    }
  }, [campaignId, merge])

  useEffect(() => {
    if (!enabled) return
    let timer: ReturnType<typeof setInterval> | null = null

    const startInterval = () => {
      if (timer) return
      timer = setInterval(() => void poll(), POLL_MS)
    }
    const stopInterval = () => {
      if (!timer) return
      clearInterval(timer)
      timer = null
    }

    // Fetch once no matter what. Gating this on visibility too would leave a
    // campaign opened in a background tab stuck on "Loading" until it is focused.
    void poll()

    // The repeating poll is what pauses in the background; coming back to the
    // tab catches up immediately rather than waiting for the next tick.
    const onVisibility = () => {
      if (document.hidden) {
        stopInterval()
      } else {
        void poll()
        startInterval()
      }
    }
    document.addEventListener('visibilitychange', onVisibility)
    if (!document.hidden) startInterval()

    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      stopInterval()
    }
  }, [enabled, poll])

  /** Show a roll immediately rather than waiting for the next poll. */
  const push = useCallback((roll: Roll) => merge([roll]), [merge])

  const reset = useCallback(() => {
    since.current = 0
    setRolls([])
    setLoading(true)
    void poll()
  }, [poll])

  return { rolls, loading, error, push, reset, setError }
}
